import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';

import { SectionLabel } from './SectionLabel';
import { PartTypeActionLiveSearchItem } from '../../../../@types/partType.type';
import { LiveSearchBox, LiveSearchListItem } from '../../../../components/atoms/LiveSearchBox';
import { DropdownItem, SelectMenu } from '../../../../components/atoms/SelectMenu';
import { useSearchPartsOrPartTypes } from '../../../../queries/live-search-query';
import { SECTION_LABEL_SX } from '../constants';

export interface PartDetailsSectionProps {
  // Serial
  reportedSerialNumber: string;
  reportedPartTypeName: string;
  serialRequired: boolean;
  // Scope passed to the part search — constrains to the chosen client/contract if
  // the user picked one; otherwise an empty search across visible parts.
  clientId: number;
  contractId: number;
  onSerialChange: (
    value: string,
    partId?: number,
    partTypeName?: string,
    partData?: PartTypeActionLiveSearchItem
  ) => void;
  serialError?: string;
  // Model
  reportedModel: string;
  modelOptions: DropdownItem[];
  modelRequired: boolean;
  onModelChange: (value: string) => void;
  modelError?: string;
  // Fault
  mainFaultId: string;
  descriptionId: string;
  mainFaultOptions: DropdownItem[];
  descriptionOptions: DropdownItem[];
  descriptionRequired: boolean;
  faultsLoading: boolean;
  hasNoFaults: boolean;
  faultRequired: boolean;
  onMainFaultChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  // Shared
  validate: boolean;
  // Serial can always be used — it's the cheapest way to seed the rest of the form.
  disabled?: boolean;
  // Model + Fault + Description need a client context (mapping / hierarchy) before they resolve.
  contextDisabled?: boolean;
}

const PartSearchItem: React.FC<{ data: PartTypeActionLiveSearchItem }> = ({ data }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>{data.serial1 || data.name}</Typography>
    {data.partTypeName && (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
        {data.partTypeName}
      </Typography>
    )}
  </Box>
);

export const PartDetailsSection: React.FC<PartDetailsSectionProps> = (props) => {
  const {
    reportedSerialNumber,
    reportedPartTypeName,
    serialRequired,
    clientId,
    contractId,
    onSerialChange,
    serialError,
    reportedModel,
    modelOptions,
    modelRequired,
    onModelChange,
    modelError,
    mainFaultId,
    descriptionId,
    mainFaultOptions,
    descriptionOptions,
    descriptionRequired,
    faultsLoading,
    hasNoFaults,
    faultRequired,
    onMainFaultChange,
    onDescriptionChange,
    validate,
    disabled,
    contextDisabled,
  } = props;

  const searchParts = useSearchPartsOrPartTypes();

  const serialCell = (
    <Box>
      <SectionLabel label="Reported Serial" required={serialRequired} helper={serialError}>
        <LiveSearchBox
          title="Reported Serial"
          hideInlineLabel
          timeOffset={400}
          isRequired={serialRequired}
          isError={!!serialError}
          disabled={disabled}
          value={reportedSerialNumber || ''}
          onClearValue={() => onSerialChange('', undefined, undefined, undefined)}
          renderItem={(sp, option) => (
            <LiveSearchListItem {...sp}>
              <PartSearchItem data={option} />
            </LiveSearchListItem>
          )}
          onChange={(data: PartTypeActionLiveSearchItem) =>
            onSerialChange(
              data?.serial1 || data?.name || '',
              data?.partId,
              data?.partTypeName,
              data
            )
          }
          onApiInvoke={async (name: string) => {
            const results = await searchParts.mutateAsync({
              name,
              serialiseType: '',
              partTypeId: '',
              clientContractIds: [clientId || 0, contractId || 0],
              locationId: 0,
              allowedPartLocationType: 'node',
              partActionType: 'install',
              searchType: 'part',
            });
            // Keep only real serialised parts (rows with a partId); part-type
            // rows would be meaningless here since the user is picking a serial.
            return results.filter((r) => !!r.partId);
          }}
        />
      </SectionLabel>
      {reportedPartTypeName && (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 0.5 }}>
          Part type: {reportedPartTypeName}
        </Typography>
      )}
    </Box>
  );

  const contextLocked = !!contextDisabled;

  const modelCell = (
    <SectionLabel
      label="Reported Model"
      required={modelRequired}
      helper={modelError}
      locked={contextLocked}
    >
      <SelectMenu
        id="create-service-reported-model"
        label="Reported Model"
        selectedValue={reportedModel}
        onChange={onModelChange}
        items={modelOptions}
        disabled={disabled || contextLocked || modelOptions.length === 0}
        validate={validate && modelRequired && !reportedModel}
        hideInlineLabel
      />
    </SectionLabel>
  );

  const faultCell = (() => {
    if (faultsLoading)
      return (
        <Box>
          <Typography sx={SECTION_LABEL_SX}>Reported Fault{faultRequired ? ' *' : ''}</Typography>
          <CircularProgress size={20} />
        </Box>
      );
    // Without a client/contract/brand there's nothing to look up — show the empty
    // select greyed out instead of a misleading "no faults configured" alert.
    if (hasNoFaults && !contextLocked)
      return (
        <SectionLabel label="Reported Fault" required={faultRequired}>
          <Alert severity="info" sx={{ fontSize: '13px' }}>
            No reported faults are configured for this selection.
          </Alert>
        </SectionLabel>
      );
    return (
      <SectionLabel label="Reported Fault" required={faultRequired} locked={contextLocked}>
        <SelectMenu
          id="create-service-reported-fault"
          label="Reported Fault"
          items={mainFaultOptions}
          selectedValue={mainFaultId}
          onChange={onMainFaultChange}
          required={faultRequired}
          validate={validate && faultRequired && !mainFaultId}
          disabled={disabled || contextLocked}
          hideInlineLabel
        />
      </SectionLabel>
    );
  })();

  const descriptionCell = descriptionRequired ? (
    <SectionLabel label="Fault Description" required locked={contextLocked}>
      <SelectMenu
        id="create-service-fault-description"
        label="Fault Description"
        items={descriptionOptions}
        selectedValue={descriptionId}
        onChange={onDescriptionChange}
        required
        validate={validate && !descriptionId}
        disabled={disabled || contextLocked || !mainFaultId}
        hideInlineLabel
      />
    </SectionLabel>
  ) : null;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        columnGap: 2,
        rowGap: 1.5,
      }}
    >
      {serialCell}
      {modelCell}
      {faultCell}
      {descriptionCell}
    </Box>
  );
};
